<template>
  <div
    class="page-section-nav-links"
    :class="{ 'is-sticky': isSticky, 'mobile-hidden': !isMobileVisible }"
  >
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="page-section-nav-links-content" v-if="fields?.list?.length > 0">
        <Swiper
          class="nav-links-list"
          :slides-per-view="'auto'"
          :space-between="0"
          :free-mode="true"
          :modules="[FreeMode]"
          @swiper="onSwiper"
        >
          <SwiperSlide
            v-for="(item, index) in fields?.list"
            :key="index"
            class="nav-links-item"
            :class="{ active: activeItemId == item?.fields?.itemId?.value }"
            @click="scrollToSection(item?.fields?.itemId?.value, index)"
          >
            <JssRichText class="nav-links-text" :field="item?.fields?.text" />
          </SwiperSlide>
        </Swiper>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { FreeMode } from 'swiper/modules';
import 'swiper/css';
import useScrollY from '@/hooks/useScrollY';

export default {
  name: 'PageSectionNavLinks',
  components: { Swiper, SwiperSlide },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const { scrollTop } = useScrollY();
    const isSticky = computed(() => scrollTop.value > 120);
    const isMobileVisible = computed(() => scrollTop.value > 100);
    const activeItemId = ref('');
    const isClickActive = ref(false);
    const swiperInstance = ref(null);

    const onSwiper = (swiper) => {
      swiperInstance.value = swiper;
    };

    // 内容溢出可视区域时，点击导航项让 swiper 自动切换到上一个/下一个，
    // 提示用户还存在更多二级菜单
    const maybeSlideToReveal = (index) => {
      const swiper = swiperInstance.value;
      if (!swiper) return;
      // 仅当导航内容总宽度超出可视区域时才触发
      if (swiper.isBeginning && swiper.isEnd) return;

      const slides = swiper.slides || [];
      const slide = slides[index];
      if (!slide) return;

      const swiperRect = swiper.el.getBoundingClientRect();
      const next = slides[index + 1];
      const prev = slides[index - 1];
      const tolerance = 1;
      const nextNotVisible =
        next && next.getBoundingClientRect().right > swiperRect.right + tolerance;
      const prevNotVisible =
        prev && prev.getBoundingClientRect().left < swiperRect.left - tolerance;

      if (nextNotVisible && !swiper.isEnd) {
        swiper.slideNext();
      } else if (prevNotVisible && !swiper.isBeginning) {
        swiper.slidePrev();
      }
    };

    const scrollToSection = (itemId, index) => {
      if (!itemId) return;
      activeItemId.value = itemId;
      isClickActive.value = true;

      const targetElement = document.getElementById(itemId);
      if (targetElement) {
        const offsetTop = targetElement.offsetTop - 130;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        });
      }

      maybeSlideToReveal(index);
    };

    const handleScroll = () => {
      const navItems = props.fields?.list || [];
      const scrollPosition = window.scrollY + 150;

      // 如果处于点击高亮状态，检查是否已滚动到其他区域
      if (isClickActive.value && activeItemId.value) {
        const currentElement = document.getElementById(activeItemId.value);
        if (currentElement) {
          const currentTop = currentElement.offsetTop;
          const currentHeight = currentElement.offsetHeight;
          // 如果滚动位置已离开当前高亮元素的区域，取消点击高亮状态
          if (scrollPosition < currentTop || scrollPosition > currentTop + currentHeight + 100) {
            isClickActive.value = false;
          }
        }
      }

      // 非点击高亮状态下，根据滚动位置自动高亮对应的导航项
      if (!isClickActive.value) {
        for (let i = navItems.length - 1; i >= 0; i--) {
          const itemId = navItems[i].fields?.itemId?.value;
          if (!itemId) continue;

          const targetElement = document.getElementById(itemId);
          if (targetElement) {
            const elementTop = targetElement.offsetTop;
            if (scrollPosition >= elementTop) {
              activeItemId.value = itemId;
              break;
            }
          }
        }
      }
    };

    onMounted(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', handleScroll);
    });

    return {
      isSticky,
      isMobileVisible,
      activeItemId,
      scrollToSection,
      onSwiper,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.page-section-nav-links {
  position: relative;
  width: 100%;
  transition:
    background-color 0.3s ease,
    box-shadow 0.3s ease;

  &.is-sticky {
    position: sticky;
    top: 94px;
    z-index: 998;
    background: $white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &.is-sticky {
    top: 54px;
  }

  &.mobile-hidden {
    display: none;
  }

  .page-section-nav-links-content {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    border-bottom: 1px solid #ccc;

    .nav-links-list {
      margin: 0;
      padding: 0;
      list-style: none;
      width: 100%;
      overflow: hidden;

      .nav-links-item {
        position: relative;
        padding: 0 16px;
        cursor: pointer;
        flex-shrink: 0;
        width: auto;
        white-space: nowrap;

        &:first-child {
          padding-left: 0;
        }

        &.active {
          .nav-links-text {
            color: $blue-night;

            &::after {
              content: '';
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              height: 4px;
              background-color: $blue-night;
            }
          }
        }

        .nav-links-text {
          display: inline-block;
          position: relative;
          padding: 12px 0;
          font-size: 14px;
          line-height: 1.5;
          color: #404040;
          text-decoration: none;
          transition: color 0.3s ease;
        }
      }
    }
  }

  @include tablet-landscape {
    &.is-sticky {
      top: 58px;
    }

    .page-section-nav-links-content {
      .nav-links-list {
        .nav-links-item {
          padding: 0 20px;

          &:hover {
            .nav-links-text {
              color: $blue-night;

              &::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 4px;
                background-color: $blue-night;
              }
            }
          }

          .nav-links-text {
            padding: 16px 0;
            font-size: 16px;
          }
        }
      }
    }
  }
}
</style>
